<script lang="ts" setup>
const shopStore = useShopStore()
const { fetchAllStores, loading } = useStores()
const selectedStore = ref<SFCCStore | undefined>(undefined)
const openAccordionIndex = ref<number>(0)
const route = useRoute()
const searchArea = ref<string>('')
const { locale, t } = useI18n()
const storedSearchTerm = useCookie(VSF_MHJ_MY_STORE_SEARCH_TERM)

provide('location', LOCATIONS.stores)

const stores = computed(() => {
  return shopStore.storesList?.filter((store: SFCCStore) => store?.c_storeStatus !== 'Closed') || []
})

const getAllStores = async () => {
  handleSubmitDataLayer({
    submitType: 'view all',
    searchValue: localeConfig[locale.value].mainPostcode,
    locale: locale.value,
    group: 'store locator',
  })
  // storesList holds all stores initially, but may also change to hold search and filter results elsewhere
  shopStore.storesList = await fetchAllStores()
  // allStoresList is a shallow copy of all stores served as a cache
  shopStore.allStoresList = [...shopStore.storesList]
  searchArea.value = ` ${t('store_page.text_result3')} ${localeConfig[locale.value].country}`
  handleResultViewDataLayer({
    submitType: 'view all',
    storeResults: shopStore.storesList,
    storeStockResults: { stores: [] },
    searchValue: localeConfig[locale.value].mainPostcode,
    locale: locale.value,
    group: 'store locator',
  })
}

const handleStoreSelection = async (store: SFCCStore) => {
  selectedStore.value = store
  await nextTick()
  document
    .querySelector('.stores__list .store-item--selected')
    ?.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' })
}

function handleToggle(index: number, isOpen: boolean) {
  if (isOpen) {
    openAccordionIndex.value = index
  }
}

async function handleSearch() {
  selectedStore.value = undefined
  openAccordionIndex.value = 0
  await nextTick()
  const storesList = document.querySelector('.stores__list')
  if (storesList) {
    storesList.scrollTop = 0
  }
}

watch(
  () => storedSearchTerm.value,
  (newSearchTerm) => {
    if (!newSearchTerm) return

    if (newSearchTerm === 'use my location') {
      searchArea.value = ` ${t('store_page.text_result2')}`
    } else {
      searchArea.value = ` ${t('store_page.text_result')} "${newSearchTerm}"`
    }
  },
  { immediate: true },
)

useSeoHead({
  title: t('store_page.seo.title', { country: mapCountryCodeToCountry(localeConfig[locale.value].country) }),
  description: t('store_page.seo.description', {
    country: mapCountryCodeToCountry(localeConfig[locale.value].country),
  }),
  gtmPageType: 'stores',
  locale: locale.value,
  path: route.path,
})

usePageContentType().setContentType('store locator')

onMounted(() => {
  onMountedFirstLoadHandler()
})
</script>

<template>
  <div class="stores container">
    <LoadingSpinner v-if="loading" />
    <h1 class="stores__title">{{ $t('store_page.title') }}</h1>
    <div class="stores__subtitle">
      {{ $t('store_page.subtitle') }} {{ $t('store_page.subtitle2') }}
      <Button variant="text" @click="getAllStores">{{ $t('store_page.store_list_link') }}</Button>
    </div>

    <div class="stores__content">
      <div class="stores__search">
        <SearchStores :stores @search="handleSearch" />
        <p v-if="stores.length > 0 && searchArea" class="stores__list-result">
          {{
            $t('store_page.text_result_wrapper', {
              digit: stores.length,
              location: searchArea,
            })
          }}
        </p>
      </div>

      <div class="stores__map">
        <StoreMap :stores :selected-store="selectedStore" @store-selected="handleStoreSelection" />
      </div>

      <div class="stores__list">
        <StoreSelection
          v-for="(store, index) in stores"
          :key="store.id"
          :store="store"
          :index="index"
          :selected-store="selectedStore"
          :open-accordion-index="openAccordionIndex"
          :class="{ 'store-item--selected': selectedStore?.id === store.id }"
          @select-store="handleStoreSelection"
          @toggle="(isOpen) => handleToggle(index, isOpen)"
        />
      </div>
    </div>
  </div>
</template>

<style lang="postcss">
.stores {
  padding: 0 var(--space-50);
  .stores__title {
    padding-top: var(--space-60);
    padding-bottom: var(--space-60);
    margin-bottom: 0;
    @media (--tablet) {
      padding-top: var(--space-70);
      padding-bottom: var(--space-70);
    }
  }
  .stores__subtitle {
    padding-bottom: var(--space-60);
  }
  .stores__content {
    display: grid;
    row-gap: var(--space-50);
    column-gap: var(--space-60);
    grid-template-columns: 1fr;
    .stores__search {
      order: 1;
    }
    .stores__map {
      height: 400px;
      order: 2;
      overflow: hidden;

      @media (--tablet) {
        height: 60vh;
        min-height: 500px;
      }
    }
    .stores__list {
      order: 3;
      display: flex;
      flex-direction: column;
      gap: var(--space-50);
      padding-bottom: var(--space-60);
    }
    @media (--tablet) {
      grid-template-columns: 24rem 1fr;
      grid-template-rows: auto auto;
      .stores__search {
        order: 1;
        grid-column: 1;
        grid-row: 1;
      }
      .stores__list {
        order: 2;
        grid-column: 1;
        grid-row: 2;
        max-height: 60vh;
        overflow-y: auto;
      }
      .stores__map {
        order: 3;
        grid-column: 2;
        grid-row: 1 / 3; /* Span both rows */
      }
    }
  }
  .stores__list-result {
    margin-bottom: 0;
    margin-top: var(--space-60);
    text-align: center;
  }

  .store-item--selected {
    border-color: var(--primary);
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
  }
}
</style>
